import { useMeFavouritePartner, useSettings } from '@capsicum/common';
import { type NCWTokenBalanceType, useAvailableMarketsPerPartnerQuery, usePartners } from '@capsicum/socios-core';
import { useMemo } from 'react';

import {
	type NCWalletTokenCardsBalanceCommonEntry,
	type NCWalletTokenCardsBalanceEntry,
	type NCWalletTokenCardsEntry
} from '../../models';
import { useEmptyNCWalletTokenBalanceCreator } from '../useEmptyNCWalletTokenBalanceCreator';

import { TOP_MOVING_TOKENS_MAX_ENTRIES } from './useNCWalletTopMovingTokens';

type UseNCWalletTokenListProps = {
	balances: Array<NCWTokenBalanceType>;
};

type FavouriteTeamNCWalletTokenCardsEntry = [] | [NCWalletTokenCardsEntry];
type FavouriteTeamNCWalletTokenCardsBalance = [] | [NCWalletTokenCardsBalanceEntry];

export const useNCWalletTokenList = ({ balances }: UseNCWalletTokenListProps) => {
	const { arePartnersLoading, getPartnerBy } = usePartners();
	const { favoritePartner, isFavouritePartnerLoading } = useMeFavouritePartner({ enabled: balances.length > 0 });

	const { create: createEmptyNCWalletTokenBalance, isLoading: isEmptyNCWalletTokenBalanceCreatorLoading } =
		useEmptyNCWalletTokenBalanceCreator();

	const favoritePartnerID = favoritePartner?.id;

	const { data: favouritePartnerMarkets, isLoading: isFavouritePartnerMarketLoading } =
		useAvailableMarketsPerPartnerQuery(({ data }) => data, {
			enabled: !!favoritePartnerID,
			query: {
				partnerIds: [favoritePartnerID ?? ''] // This will never happen because of the `enabled`.
			}
		});

	const {
		areSettingsLoading,
		settings: {
			isExternalCurrencyInWalletEnabled,
			isWalletFavouriteTeamFTSectionEnabled,
			isWalletTopMovingFTSectionEnabled
		}
	} = useSettings();

	const { favouriteTeamBalance, nonFavouriteTeamBalances } = useMemo(() => {
		return balances.reduce<{
			favouriteTeamBalance: FavouriteTeamNCWalletTokenCardsBalance;
			nonFavouriteTeamBalances: Array<NCWalletTokenCardsBalanceEntry>;
		}>(
			(
				balances,
				{
					amount,
					currencyId,
					isExternalToken,
					shortCode,
					stakedAmount,
					stakeOperationId,
					symbol,
					tokenCardData,
					type
				}
			) => {
				const isNonDecimalVoteToken = type === 'nonDecimalVoteToken';
				const isExternalTokenEnabled = isExternalToken && isExternalCurrencyInWalletEnabled;

				const partnerID =
					getPartnerBy(partner => {
						return isNonDecimalVoteToken
							? partner.tokenSymbol === symbol
							: partner.currencyId === currencyId;
					})?.id || null;

				const commonBalanceProps: NCWalletTokenCardsBalanceCommonEntry = {
					amount,
					currencyId,
					formattedAltBalance: tokenCardData.formattedAltBalance,
					formattedDefaultBalance: tokenCardData.formattedDefaultBalance,
					shortCode,
					stakedAmount,
					stakeOperationId,
					title: tokenCardData.title,
					tokenBalance: tokenCardData.tokenBalance ?? '',
					tokenImgBlurhash: tokenCardData.tokenImgBlurhash,
					tokenImgUrl: tokenCardData.tokenImgUrl,
					type: 'balance',
					usableFormattedAltBalance: tokenCardData.usableFormattedAltBalance,
					usableFormattedDefaultBalance: tokenCardData.usableFormattedDefaultBalance,
					usableTokenBalance: tokenCardData.usableTokenBalance ?? ''
				};

				let balance: NCWalletTokenCardsBalanceEntry | null = null;

				if (partnerID) {
					balance = {
						...commonBalanceProps,
						partnerID,
						tokenType: isNonDecimalVoteToken ? 'non-decimal-vote-token' : 'partner'
					};
				} else if (isExternalTokenEnabled) {
					balance = {
						...commonBalanceProps,
						tokenType: 'external-token'
					};
				}

				if (balance) {
					if (favoritePartnerID && partnerID === favoritePartnerID) {
						balances.favouriteTeamBalance = [balance];
					} else {
						balances.nonFavouriteTeamBalances.push(balance);
					}
				}

				return balances;
			},
			{
				favouriteTeamBalance: [],
				nonFavouriteTeamBalances: []
			}
		);
	}, [balances, getPartnerBy, favoritePartnerID, isExternalCurrencyInWalletEnabled]);

	const favouriteTeamEntry = useMemo<FavouriteTeamNCWalletTokenCardsEntry>(() => {
		if (favouriteTeamBalance.length > 0) return favouriteTeamBalance;

		if (
			!favoritePartner ||
			!favoritePartner.id ||
			!favouritePartnerMarkets ||
			isEmptyNCWalletTokenBalanceCreatorLoading
		)
			return [];

		const partner = favouritePartnerMarkets[0];

		if (partner) {
			const balance = createEmptyNCWalletTokenBalance({ partner });

			return balance ? [balance] : [];
		}

		return [
			{
				partnerID: favoritePartner.id,
				title: favoritePartner.name,
				tokenImgUrl: favoritePartner.images?.partnerLogo?.url,
				type: 'no-market'
			}
		];
	}, [
		favoritePartner,
		favouriteTeamBalance,
		favouritePartnerMarkets,
		createEmptyNCWalletTokenBalance,
		isEmptyNCWalletTokenBalanceCreatorLoading
	]);

	const myTokenCardsEntries = useMemo(
		() =>
			isWalletFavouriteTeamFTSectionEnabled ||
			!favouriteTeamEntry[0] ||
			favouriteTeamEntry[0].type !== 'balance' ||
			favouriteTeamEntry[0].amount === 0
				? nonFavouriteTeamBalances
				: [...favouriteTeamEntry, ...nonFavouriteTeamBalances],
		[favouriteTeamEntry, nonFavouriteTeamBalances, isWalletFavouriteTeamFTSectionEnabled]
	);

	const myTokenCardsTokensShortCodes = useMemo(
		() =>
			myTokenCardsEntries.reduce<Array<string>>((myTokenCardsTokens, token) => {
				if (token.type === 'balance') myTokenCardsTokens.push(token.shortCode);

				return myTokenCardsTokens;
			}, []),
		[myTokenCardsEntries]
	);

	return {
		favouriteTeamEntry,
		isFavouriteTeamFanTokensSectionVisible: isWalletFavouriteTeamFTSectionEnabled,
		isLoading:
			arePartnersLoading ||
			areSettingsLoading ||
			isFavouritePartnerLoading ||
			isFavouritePartnerMarketLoading ||
			isEmptyNCWalletTokenBalanceCreatorLoading,
		isTopMovingFanTokensSectionVisible:
			isWalletTopMovingFTSectionEnabled && myTokenCardsEntries.length < TOP_MOVING_TOKENS_MAX_ENTRIES,
		myTokenCardsEntries,

		myTokenCardsTokensShortCodes,

		nonFavouriteTeamBalances
	};
};
